import React, {useEffect, useState} from "react";
import axios from "axios";
import {MessageCircle, Plus, Trash2, Truck} from "lucide-react";
import * as Constants from "../../utils/Constants";
import * as UrlConstants from "../../utils/UrlConstants";
import {getAuthToken, getClientIdOfUser, showToast} from "../../utils/utility";
import Button from "../../components/ui/Button";
import TextField from "../../components/ui/TextField";
import Select from "../../components/ui/Select";
import Card, {CardHeader, CardBody} from "../../components/ui/Card";
import Spinner from "../../components/ui/Spinner";
import {cn} from "../../utils/cn";

// Mirrors eLockApp's LoginIdentifier.GLOBAL_PHONE_REGEX / the backend's
// WhatsAppAlertRecipientService pattern: 10-15 digits once the dialing code is joined in.
const PHONE_REGEX = /^\+\d{10,15}$/;

// Same set the backend's CountryTimezoneUtil maps ISO codes to dialing codes for — a
// number entered without one of these was exactly how bare local numbers (no country
// code) slipped into notification_queue and got silently marked SENT but undelivered.
const COUNTRY_CODES = [
  {iso: "IN", dial: "+91", label: "India (+91)"},
  {iso: "KE", dial: "+254", label: "Kenya (+254)"},
  {iso: "US", dial: "+1", label: "United States (+1)"},
  {iso: "GB", dial: "+44", label: "United Kingdom (+44)"},
  {iso: "AU", dial: "+61", label: "Australia (+61)"},
  {iso: "SG", dial: "+65", label: "Singapore (+65)"},
  {iso: "AE", dial: "+971", label: "UAE (+971)"},
  {iso: "SA", dial: "+966", label: "Saudi Arabia (+966)"},
  {iso: "NG", dial: "+234", label: "Nigeria (+234)"},
  {iso: "TZ", dial: "+255", label: "Tanzania (+255)"},
  {iso: "UG", dial: "+256", label: "Uganda (+256)"},
  {iso: "ZA", dial: "+27", label: "South Africa (+27)"},
  {iso: "DE", dial: "+49", label: "Germany (+49)"},
  {iso: "FR", dial: "+33", label: "France (+33)"},
  {iso: "CA", dial: "+1", label: "Canada (+1)"},
  {iso: "JP", dial: "+81", label: "Japan (+81)"},
];

const MAX_RECIPIENTS = 3;

/**
 * Lets a tenant admin register up to 3 additional WhatsApp numbers (beyond the
 * client's primary contact) to receive GT06 GPS Tracker alerts — overspeed,
 * tamper, and battery-critical, delivered in real time; low battery / GPS
 * lost / device offline are batched into the daily digest instead.
 *
 * Backend: WhatsAppAlertRecipientController
 * (GET/POST/DELETE /api/v1/{clientId}/whatsapp-recipients[/{recipientId}]).
 */
const WhatsAppRecipients = () => {
  const [recipients, setRecipients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialCode, setDialCode] = useState(COUNTRY_CODES[0].dial);
  const [phone, setPhone] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [saving, setSaving] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  const clientId = getClientIdOfUser();
  const baseUrl = `${Constants.BASE_URL}${Constants.API_V1}/${clientId}/${UrlConstants.WHATSAPP_RECIPIENTS}`;

  const authHeader = () => {
    axios.defaults.headers.common["Authorization"] = `${Constants.BEARER} ${getAuthToken()}`;
  };

  const fetchRecipients = () => {
    setLoading(true);
    authHeader();
    axios.get(baseUrl)
      .then((res) => setRecipients(res.data?.responseData || []))
      .catch((err) => {
        console.error("WhatsApp recipients load failed:", err?.response?.status, err?.response?.data || err?.message);
        showToast(err?.response?.data?.responseMessage || err?.message || "Failed to load WhatsApp recipients", {type: "error"});
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchRecipients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const atMax = recipients.length >= MAX_RECIPIENTS;

  const handleAdd = (e) => {
    e.preventDefault();
    // Local digits only — the country code is a required, separately-selected field so a
    // number can never be saved without one (this was exactly how bare local numbers
    // slipped into notification_queue and got silently marked SENT but never delivered).
    const localDigits = phone.trim().replace(/\D/g, "");
    const combinedPhone = `${dialCode}${localDigits}`;
    if (!PHONE_REGEX.test(combinedPhone)) {
      showToast("Enter a valid local phone number", {type: "error"});
      return;
    }
    setSaving(true);
    authHeader();
    const requestBody = {phoneNumber: combinedPhone, displayName: displayName.trim() || undefined};
    axios.post(baseUrl, {requestBody})
      .then(() => {
        setPhone("");
        setDisplayName("");
        showToast("Recipient added", {type: "success"});
        fetchRecipients();
      })
      .catch((err) => {
        console.error("WhatsApp recipient add failed:", err?.response?.status, err?.response?.data || err?.message);
        showToast(err?.response?.data?.responseMessage || err?.message || "Failed to add recipient", {type: "error"});
      })
      .finally(() => setSaving(false));
  };

  const handleRemove = (recipient) => {
    if (!window.confirm(`Remove ${recipient.displayName || recipient.phoneNumber}? They will stop receiving WhatsApp tracker alerts.`)) {
      return;
    }
    setRemovingId(recipient.id);
    authHeader();
    axios.delete(`${baseUrl}/${recipient.id}`)
      .then(() => {
        showToast("Recipient removed", {type: "success"});
        fetchRecipients();
      })
      .catch((err) => {
        console.error("WhatsApp recipient remove failed:", err?.response?.status, err?.response?.data || err?.message);
        showToast(err?.response?.data?.responseMessage || err?.message || "Failed to remove recipient", {type: "error"});
      })
      .finally(() => setRemovingId(null));
  };

  if (loading) {
    return (
      <div className="py-16 flex items-center justify-center gap-3 text-ink-500">
        <Spinner size="md" /><span className="text-sm">Loading WhatsApp recipients…</span>
      </div>
    );
  }

  return (
    <div className="max-w-[900px] mx-auto space-y-6 pb-8">
      <Card>
        <CardHeader
          title="GT06 GPS Tracker WhatsApp alerts"
          subtitle="Overspeed, tamper, and battery-critical alerts are sent immediately over WhatsApp to your registered contact and up to 3 additional numbers below. Low battery, GPS lost, and device offline are batched into a daily email digest instead, to avoid message overload."
        />
        <CardBody className="space-y-4">
          {recipients.length === 0 ? (
            <p className="text-sm text-ink-500 py-6 text-center">No additional recipients yet.</p>
          ) : (
            <ul className="space-y-2">
              {recipients.map((r) => (
                <li
                  key={r.id}
                  className={cn(
                    "flex items-center gap-3 px-4 py-3 rounded-lg border border-ink-200 bg-white"
                  )}
                >
                  <div className="h-9 w-9 rounded-full bg-brand-50 flex items-center justify-center shrink-0">
                    <MessageCircle className="h-4 w-4 text-brand-700" aria-hidden="true" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-semibold text-ink-900 truncate">{r.phoneNumber}</p>
                    {r.displayName && (
                      <p className="text-xs text-ink-500 truncate">{r.displayName}</p>
                    )}
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    icon={Trash2}
                    aria-label="Remove recipient"
                    loading={removingId === r.id}
                    onClick={() => handleRemove(r)}
                    className="text-danger-600 hover:text-danger-700 hover:bg-danger-50"
                  />
                </li>
              ))}
            </ul>
          )}

          {atMax ? (
            <p className="text-sm text-ink-500 text-center py-2">
              Maximum of {MAX_RECIPIENTS} additional recipients reached. Remove one to add another.
            </p>
          ) : (
            <form onSubmit={handleAdd} className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2 border-t border-ink-100">
              <div className="sm:col-span-1 mt-4">
                <label className="text-sm font-medium text-ink-700 mb-1.5 block">Phone number</label>
                <div className="flex gap-2">
                  <Select
                    value={dialCode}
                    onChange={(e) => setDialCode(e.target.value)}
                    className="w-36 shrink-0"
                    aria-label="Country code"
                  >
                    {COUNTRY_CODES.map((c) => (
                      <option key={c.iso} value={c.dial}>{c.label}</option>
                    ))}
                  </Select>
                  <TextField
                    placeholder="9876543210"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                    required
                    className="flex-1"
                  />
                </div>
              </div>
              <TextField
                label="Display name (optional)"
                placeholder="e.g. Fleet Manager"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="sm:col-span-1 mt-4"
              />
              <div className="sm:col-span-2 flex justify-end">
                <Button type="submit" variant="primary" icon={Plus} loading={saving}>
                  Add recipient
                </Button>
              </div>
            </form>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader
          title="Rate limits"
          subtitle="These apply automatically to protect your WhatsApp Business API budget — no configuration needed."
        />
        <CardBody>
          <ul className="text-sm text-ink-600 space-y-2">
            <li className="flex items-start gap-2">
              <Truck className="h-4 w-4 text-brand-600 mt-0.5 shrink-0" aria-hidden="true" />
              Max 5 WhatsApp messages per device per hour
            </li>
            <li className="flex items-start gap-2">
              <Truck className="h-4 w-4 text-brand-600 mt-0.5 shrink-0" aria-hidden="true" />
              Max 10 WhatsApp messages per device per day
            </li>
            <li className="flex items-start gap-2">
              <Truck className="h-4 w-4 text-brand-600 mt-0.5 shrink-0" aria-hidden="true" />
              30-minute cooldown between repeated alerts of the same type on the same device
            </li>
          </ul>
        </CardBody>
      </Card>
    </div>
  );
};

export default WhatsAppRecipients;
